import {
  Box,
  Button,
  Typography,
  Hidden,
  IconButton,
  Tooltip,
  Grid,
  Stack,
  Switch,
  Dialog,
  useTheme,
  Badge,
  Theme,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  Drawer
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { useEffect, useState, useMemo, ChangeEvent } from 'react';
import ManagementTable, { ColumnType } from './ManagementTable';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { DataTable } from '../DataTable';
import { Link as RouterLink } from 'react-router-dom';
import { editRM, getRMsList, getSubRoleDetails, getSubRoles } from '../../redux-store/actions/userManagement';
import { newRmType, RmsType, addRmDetails, GetSubRoleDetailsResponseBody } from '../../redux-store/types/api-types';
import { ConfirmationDialog } from '../commonComponents';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { CheckIcon, FilterCancel, HierarchyStructure, UnCheckIcon } from '../../assets/SvgIcons';
import { PaginationObj } from '../investorOverview/types';
import TablePaginationComponent from '../investorOverview/common/customTable/TablePaginationComponent';
import { AccordionFilter } from '../investors/filters';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

export const AccessHeader = (): JSX.Element => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  return (
    <>
      <Box display={'flex'} flexDirection={'column'}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: `${theme.typography.fontFamily}`,
            fontSize: 14,
            color: 'primary.main',
            fontWeight: 500,
          }}
          component="span">
          Access
          <InfoOutlinedIcon
            color="info"
            fontSize="small"
            onClick={handleOpen}
            sx={{ position: 'relative', top: 4 }}
          />
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontFamily: `${theme.typography.fontFamily}`,
            fontSize: 12,
            color: 'primary.main',
            fontWeight: 500,
          }}>
          Enable/Disable
        </Typography>
      </Box>

      <Dialog
        onClose={handleClose}
        open={open}
        sx={{
          '.MuiPaper-root ': {
            maxWidth: 550,
            p: { xs: 2, sm: '15px 20px 45px' },
            borderRadius: '10px',
            height: 'unset',
            overflowY: 'auto',
          },
        }}>
        <IconButton onClick={handleClose} sx={{ alignSelf: 'flex-end' }}>
          <CloseIcon fontSize="medium" />
        </IconButton>
        <Box sx={{ mb: 2, textAlign: 'center', '.MuiSvgIcon-root': { fontSize: '3.1875rem' } }}>
          <InfoOutlinedIcon color="info" fontSize="large" />
        </Box>
        <Box
          sx={{
            width: { xs: '100%', sm: '80%' },
            mx: 'auto',
            '.MuiTypography-root': {
              fontSize: 20,
              fontWeight: 500,
              textAlign: 'center',
            },
          }}>
          <Typography sx={{ color: 'secondary.main', lineHeight: '35px' }}>
            Disabling the access of the user will log him out of the active session and he will not
            be able to access the portal.
          </Typography>
          <Typography sx={{ color: 'secondary.main', lineHeight: '35px', mt: 2 }}>
            Please make sure there are no draft and In-progress applications for the user before
            disabling the user .
          </Typography>
        </Box>
      </Dialog>
    </>
  );
};

const headers = [
  {
    header: 'RM Name',
    renderCell: (row: RmsType) => {
      return (
        <Typography
          variant="h5"
          sx={{
            color: '#61D1D6',
            textDecoration: 'none',
            fontWeight: 500,
            fontSize: '14px',
            ':hover': {
              color: 'primary.main',
            },
          }}
          component={RouterLink}
          to={{ pathname: 'relationship-manager-details', state: { rmId: row.id } }}>
          {row.user?.firstName || 'N/A'}
        </Typography>
      );
    },
  },
  {
    header: 'Email Id',
    label: 'email',
    valueGetter: (row: RmsType) => row.user?.email || 'N/A',
  },
  {
    header: 'Contact Details',
    label: 'contact',
    valueGetter: (row: RmsType) => row.user?.phone || 'N/A',
  },
  {
    header: 'PAN',
    label: 'panNumber',
    valueGetter: (row: RmsType) => row.panNumber || 'N/A',
  },
  {
    header: 'Role/Sub Role',
    label: 'userRole',
    valueGetter: (row: RmsType) => `${(row?.user?.role ?? "-")}/${(row?.userRole ?? "-")}`.toUpperCase(),
  },
  {
    header: 'EMP Code',
    label: 'empCode',
    valueGetter: (row: RmsType) => row.empCode || 'N/A',
  },
];

export default function RelationshipManagers(): JSX.Element {
  const dispatch = useDispatch();
  const [isLoading, setLoading] = useState(false);
  const [RMData, setRMData] = useState<RmsType[]>();
  const history = useHistory();
  const [openDialog, setDialog] = useState<{ rmRowData: any } | null>(null);
  const [isFilterOpen, setFilterOpen] = useState<boolean>(false);
  const [expandedFilter, setExpandedFilter] = useState<string>("subrole")
  const [subRolesFilterData, setSubRolesFilterData] = useState<Array<{ label: string; value: string; checked: boolean }>>([])
  const [appliedFilters, setAppliedFilters] = useState<Array<{ label: string; value: string; }>>([])
  const [tablePaginationObj, setTablePaginationObj] = useState<PaginationObj>({
    currentPage: 0,
    rowsPerPage: 10,
    totalRows: 0
  })

  const theme = useTheme();

  const fetchSchemesTableData = async ({
    role,
    currentPage,
    rowsPerPage
  }: {
    role: string;
    currentPage: number;
    rowsPerPage: number;
  }) => {
    const payload = {
      role,
      currentPage,
      rowsPerPage
    }

    const { rms = [], totalCount = 0 } = await (dispatch(getSubRoleDetails(payload)) as unknown as Promise<GetSubRoleDetailsResponseBody>)


    setRMData(rms ?? [])

    setTablePaginationObj({
      currentPage: currentPage - 1,
      rowsPerPage,
      totalRows: totalCount ?? 0
    })
  }

  const fetchSubRolesData = async () => {
    const response = await (dispatch(getSubRoles()) as unknown as Promise<Array<string>>)

    const modifiedResponse = (response ?? []).map(ele => ({
      label: ele,
      value: ele,
      checked: false
    }))

    setSubRolesFilterData(modifiedResponse)
  }

  useEffect(() => {
    let isComponentActive = true;
    (async function () {
      try {
        if (!isComponentActive) {
          return;
        }

        setLoading(true);

        await Promise.allSettled([
          fetchSubRolesData(),
          fetchSchemesTableData({
            role: "",
            currentPage: 1,
            rowsPerPage: 10
          })])
      } catch (e) {
        setLoading(false);
        console.log((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      isComponentActive = false;
    };
  }, []);

  const AccessViewOrEditCell = [
    {
      header: AccessHeader,
      renderCell: (row: any): JSX.Element => {
        return (
          <>
            <Stack
              sx={{
                alignItems: { xs: 'baseline', sm: 'center' },
                '.MuiSwitch-root': {
                  paddingLeft: { xs: '0px', sm: '12px' },
                  width: { xs: '52px', sm: '58px' },
                },
                '.MuiSwitch-switchBase': { paddingLeft: { xs: '0px', sm: '9px' } },
              }}>
              <Switch
                sx={{ '.MuiSwitch-switchBase': { color: 'black' } }}
                checked={row?.isActive === '1'}
                onChange={() => setDialog({ rmRowData: row })}
              />
            </Stack>
          </>
        );
      },
    },
    // {
    //   header: '',
    //   renderCell: (row: RmsType): JSX.Element => {
    //     return (
    //       <>
    //         {row.isActive ? (
    //           <Tooltip title="Edit">
    //             <IconButton
    //               // onClick={() => {
    //               //  history.push(`/application`, { id: row.id });
    //               // }}
    //               component={RouterLink}
    //               to={{ pathname: 'edit-rm-details', state: { rmId: row.id } }}
    //               sx={{
    //                 ':hover': {
    //                   bgcolor: '#F4FCFC',
    //                 },
    //                 '&.Mui-disabled': {
    //                   cursor: 'pointer',
    //                   pointerEvents: 'all',
    //                 },
    //               }}>
    //               <EditIcon />
    //             </IconButton>
    //           </Tooltip>
    //         ) : (
    //           <Typography
    //             variant="h5"
    //             component={RouterLink}
    //             to={{ pathname: 'edit-rm-details', state: { rmId: row.id } }}
    //             sx={{
    //               color: '#61D1D6',
    //               textDecoration: 'none',
    //               fontWeight: 500,
    //               fontSize: '14px',
    //               ':hover': {
    //                 color: 'primary.main',
    //               },
    //             }}>
    //             View Details
    //           </Typography>
    //         )}
    //       </>
    //     );
    //   },
    // },
    // {
    //   header: "View SRM's",
    //   renderCell: (row: RmsType): JSX.Element => {
    //     return (
    //       <Typography
    //         variant="h5"
    //         component={RouterLink}
    //         to={{ pathname: 'srms', state: { rmId: row.id } }}
    //         sx={{
    //           color: '#61D1D6',
    //           textDecoration: 'none',
    //           fontWeight: 500,
    //           fontSize: '14px',
    //           ':hover': {
    //             color: 'primary.main',
    //           },
    //         }}>
    //         View
    //       </Typography>
    //     );
    //   },
    // },
    {
      header: "View Hierarchy",
      renderCell: (row: RmsType): JSX.Element => {
        return (
          <IconButton
            component={RouterLink}
            to={{ pathname: 'view-hierarchy', state: { rmId: row.iwappRmId } }}
          >
            <HierarchyStructure />
          </IconButton>
        );
      },
    },
  ];

  const handleFilterOpen = async () => {

    const appliedFiltersArr = appliedFilters.map(obj => obj.value)

    setSubRolesFilterData(prevState => prevState.map(obj => ({
      ...obj,
      checked: appliedFiltersArr.includes(obj.value)
    })))

    setExpandedFilter("subrole")
    setFilterOpen(true)
  };

  const handleFilterClose = async () => {
    setFilterOpen(false)
  };

  const handleFilterAccordian = (
    event: React.SyntheticEvent<Element, Event>,
    isExpanded: boolean,
    panel: string
  ) => {
    setExpandedFilter(isExpanded ? panel : "");
  };

  const handleSubRoleFilterSelection = (checked: boolean, filterName: string) => {
    setSubRolesFilterData(prevData => prevData.map(obj => {
      if (obj.value === filterName) {
        return { ...obj, checked }
      } else return obj
    }))
  }

  const handleFilterClearAll = async () => {
    try {
      setLoading(true);
      setFilterOpen(false)

      setSubRolesFilterData(prevState => prevState.map(obj => ({ ...obj, checked: false })))
      setAppliedFilters([])

      await fetchSchemesTableData({
        role: "",
        currentPage: 1,
        rowsPerPage: tablePaginationObj.rowsPerPage
      })
    } catch (e) {
      console.log("Error: ", e)
    } finally {
      setLoading(false);
    }
  }

  const handleApplyFilters = async () => {
    try {
      setLoading(true);
      setFilterOpen(false)

      const selectedFilters = subRolesFilterData.filter(obj => obj.checked)
      setAppliedFilters(selectedFilters.map(obj => ({ label: obj.label, value: obj.value })))

      await fetchSchemesTableData({
        role: selectedFilters.map(obj => obj.value).join(","),
        currentPage: 1,
        rowsPerPage: tablePaginationObj.rowsPerPage
      })

    } catch (e) {
      console.log("Error: ", e)
    } finally {
      setLoading(false);
    }
  }

  const handlePageChange = async (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
    try {
      setLoading(true)

      const appliedFiltersArr = appliedFilters.map(obj => obj.value).join(",")

      await fetchSchemesTableData({
        role: appliedFiltersArr,
        currentPage: page + 1,
        rowsPerPage: tablePaginationObj.rowsPerPage
      })
    } catch (e) {
      console.log("Error: ", e)
    } finally {
      setLoading(false)
    }
  }

  const handleRowsPerPageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setLoading(true)

      const appliedFiltersArr = appliedFilters.map(obj => obj.value).join(",")

      await fetchSchemesTableData({
        role: appliedFiltersArr,
        currentPage: 1,
        rowsPerPage: Number(event.target.value)
      })
    } catch (e) {
      console.log("Error: ", e)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Box sx={{ bgcolor: 'common.white', px: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            pt: { xs: 2, sm: 7 },
            pb: { xs: 2, sm: 4 },
          }}>
          <Typography sx={{ fontSize: { xs: 15, sm: 25 }, fontWeight: 500 }}>
            List of Relationship Managers
          </Typography>
          <Box
            component='button'
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer'
            }}
            onClick={handleFilterOpen}
          >
            <Badge
              showZero
              badgeContent={(appliedFilters ?? []).length}
              sx={{
                '& .MuiBadge-badge': {
                  right: 0,
                  top: 0,
                  backgroundColor: theme.palette.primary[500],
                  color: theme.palette.common.white,
                  ...theme.typography.subtitle_600
                },
              }}
            >
              <FilterAltIcon
                sx={{ ...theme.typography.h3 }}
              />
            </Badge>
            <Typography
              variant='body_500'
              sx={(theme: Theme) => ({
                color: theme.palette.text.primary
              })}
            >
              Filters
            </Typography>
          </Box>
          {/* <Hidden only="xs">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                color: 'white',
                fontWeight: 500,
                fontSize: 14,
                mr: 2.5,
                py: 0.5,
                px: 3,
              }}
              onClick={() => history.push('invite-rm-details')}>
              Invite RM
            </Button>
          </Hidden>
          <Hidden smUp={true}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{
                color: 'primary.main',
                fontWeight: 500,
                fontSize: 13,
                // mr: 2.5,
                py: 0.5,
                // px: 3,
                // whiteSpace: 'nowrap',
              }}
              onClick={() => history.push('invite-rm-details')}>
              Add RM
            </Button>
          </Hidden> */}
        </Box>

        <Grid item xs={12} sm={12} lg={12}>
          {useMemo(
            () => (
              <>
                <DataTable
                  tableData={RMData || []}
                  tableHeader={[...headers, ...AccessViewOrEditCell]}
                  isLoading={isLoading}
                  renderAdditionalRow={true}
                  tableBodyCustomStyles={{
                    '.MuiTableCell-root': {
                      borderBottom: 'none',
                    },
                  }}
                  tableHeaderCustomStyles={{
                    '&:last-child th': { border: 0 },
                    '.MuiTableCell-head': {
                      fontFamily: `${theme.typography.fontFamily}`,
                      fontSize: 14,
                      fontWeight: 500,
                      color: 'primary.main',
                    },
                  }}
                />
                {(!isLoading && ((RMData ?? []).length) > 0) && (
                  <Box sx={{ marginTop: "10px" }}
                  >
                    <TablePaginationComponent
                      tableDataCount={tablePaginationObj.totalRows}
                      handlePageChange={handlePageChange}
                      handleRowsPerPageChange={handleRowsPerPageChange}
                      rowsPerPage={tablePaginationObj.rowsPerPage}
                      rowsPerPageOptions={[10, 15, 20]}
                      currentPage={tablePaginationObj.currentPage}
                    />
                  </Box>
                )}
              </>
            ),
            [headers, isLoading, RMData]
          )}
        </Grid>

        {/* <ManagementTable tableData={RMData || []} tableHeader={headers} isLoading={isLoading} /> */}
      </Box>
      <ConfirmationDialog
        message={`Are you sure you want to ${(openDialog?.rmRowData?.isActive === '1') ? 'disable' : 'enable'
          } the access to this user ?`}
        open={openDialog !== null}
        setOpen={() => setDialog(null)}
        onSave={async () => {
          setDialog(null);
          try {
            if (openDialog) {
              const updatedResponse = (await dispatch(
                editRM(openDialog.rmRowData.id as number, {
                  ...openDialog.rmRowData,
                  isActive: openDialog?.rmRowData?.isActive === "1" ? "0" : "1",
                })
              )) as unknown as RmsType;
              setRMData(
                RMData?.map((rm) => {
                  if (Number(rm.id) === Number(updatedResponse.id)) {
                    return {
                      ...rm,
                      ...updatedResponse,
                    };
                  }
                  return rm;
                })
              );
            }
          } catch (e) {
            console.error((e as Error).message);
          }
        }}
        onCancel={() => setDialog(null)}
      />
      <Drawer
        open={isFilterOpen}
        onClose={handleFilterClose}
        anchor="right"
        PaperProps={{
          sx: {
            padding: "25px",
            width: "450px"
          }
        }}
        ModalProps={{
          sx: (theme: Theme) => ({
            backgroundColor: `${theme.palette.common.black}03`
          })
        }}
      >
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
        >
          <Typography
            variant='h2'
            color={theme.palette.text.primary}
          >
            Filters
          </Typography>
          <Box>
            <Button
              variant='text'
              type='reset'
              disableTouchRipple
              onClick={handleFilterClearAll}
              style={{
                color: theme.palette.primary[500],
                fontWeight: 500,
                fontSize: 16,
              }}
            >
              Clear All
            </Button>
            <IconButton onClick={handleFilterClose}>
              <FilterCancel />
            </IconButton>
          </Box>
        </Box>
        <Box>
          <Box component={'hr'} />
          <AccordionFilter
            filterName="subrole"
            expanded={expandedFilter === 'subrole'}
            handleChange={(e, expanded, filterName) =>
              handleFilterAccordian(e, expanded, filterName)
            }>
            <RadioGroup
              aria-label="subrole"
              defaultValue=""
              name="subrole"
              sx={{ gap: '5px' }}
            >
              {subRolesFilterData
                .map((obj: { label: string; value: string; checked: boolean }) => (
                  <FormControlLabel
                    key={obj.value}
                    value={obj.value}
                    onChange={(event: React.SyntheticEvent, checked: boolean) => handleSubRoleFilterSelection(checked, obj.value)}
                    control={
                      <Checkbox
                        size="small"
                        checked={obj.checked}
                        checkedIcon={<CheckIcon />}
                        icon={<UnCheckIcon />}
                      />
                    }
                    label={obj.label}
                    title={obj.label}
                  />
                ))}
            </RadioGroup>
          </AccordionFilter>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 2,
            mt: 2,
            '& .MuiButton-root': {
              fontSize: 16,
              mx: 1,
              px: 3.5,
              py: 0.5,
            },
          }}>
          <Button
            fullWidth
            type="submit"
            variant="contained"
            sx={{
              color: 'common.white',
              '&, :hover': { bgcolor: 'primary.main' },
              fontSize: 22,
              padding: { lg: '10px 20px', xs: '10px 20px', md: '10px 20px' },
            }}
            onClick={handleApplyFilters}
          >
            Apply Filters
          </Button>
        </Box>
      </Drawer>
    </>
  );
}
